import React, {useState} from 'react';

import {Box, Button, ButtonBase, Stack, Tab, Tabs, Typography} from '@mui/material';

import {Dialog,Text} from 'components';
import ColorPicker from 'components/ColorPicker';
import {unTranslated} from 'hooks';
import {colors} from 'layout/components/toolbar/components/ColorTool';
import FlyoutButton from 'layout/components/toolbar/components/FlyoutButton';
import PlayerShirt from 'layout/components/toolbar/components/PlayerShirt';
import {useLicenseManager} from 'module/user';

const styles = {
    overallContainer: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    preview: {
        marginBottom: 0,
        paddingRight: '40px',
    },
    previewImage: {
        display: 'inline-flex',

        'svg': {
            shapeRendering: 'geometricPrecision',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale'
        },

        'svg path': {
            stroke: 'white',
            strokeWidth: '2',
            strokeLinejoin: 'round',
            strokeLinecap: 'round',
            vectorEffect: 'non-scaling-stroke',
            strokeMiterlimit: 10
        }
    },
    colorButtons: {
        display: 'flex',
        flexDirection: 'row',
    },
    colorButtonPrimary: {
        paddingRight: '40px',
    },
    colorButton: {
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        border: '2px solid',
        borderColor: '#fff',
        boxSizing: 'border-box',
    },
    tabContainer: {
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: 'grey.darkest2',
        width: '100%',
        alignItems: 'center',
        marginBottom: 2.5,
    },
    tabContainerContent: {
        padding: '0 20px 20px 20px',
    },
    colorCircleContainer: {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        witdh: '100%',
        padding: '0 20px 20px 20px',
        boxSizing: 'border-box',
    },
    takeColorContainer: {
        padding: '20px 20px 10px 20px',
    },
    takeColor: {
        color: 'white.main',
        fontSize: '18px',
        fontWeight: '500',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        padding: 2,
        borderRadius: 6,
        backgroundColor: 'red.highlight',
        '&:hover': {
            backgroundColor: 'red.highlightHover',
        },
    },
    title: {
        fontSize: '14px',
    }
};

const TeamColorPickerField = ({
    primaryColor,
    secondaryColor,
    onPrimaryColorChange,
    onSecondaryColorChange,
}) => {
    console.log('TeamColorPickerField', primaryColor, secondaryColor);
    const {hasAccess} = useLicenseManager();

    const initialSelectedPrimary = React.useMemo(
        () => (colors.some(c => c.value1 === primaryColor) ? primaryColor : 'custom'),
        [primaryColor]
    );

    const initialSelectedSecondary = React.useMemo(
        () => (colors.some(c => c.value1 === secondaryColor) ? secondaryColor : 'custom'),
        [secondaryColor]
    );

    const [selectedPrimary, setSelectedPrimary] = React.useState(initialSelectedPrimary);
    const [selectedSecondary, setSelectedSecondary] = React.useState(initialSelectedSecondary);
    const [customPrimary, setCustomPrimary] = React.useState(primaryColor);
    const [customSecondary, setCustomSecondary] = React.useState(secondaryColor);

    const [showPrimaryColor, setShowPrimaryColor] = useState(false);
    const [showSecondaryColor, setShowSecondaryColor] = useState(false);

    const [primaryTab, setPrimaryTab] = useState(selectedPrimary === 'custom' ? 'custom' : 'standard');
    const [secondaryTab, setSecondaryTab] = useState(selectedSecondary === 'custom' ? 'custom' : 'standard');

    const playerColors = {
        value1: primaryColor,
        textColor: secondaryColor
    };

    const handlePrimaryColorSelect = (color) => {
        if (color.name === 'custom') {
            setSelectedPrimary('custom');
            setPrimaryTab('custom');
            onPrimaryColorChange?.(customPrimary);
        } else {
            setSelectedPrimary(color.value1);
            setPrimaryTab('standard');
            onPrimaryColorChange?.(color.value1);
        }
    };

    const handleSecondaryColorSelect = (color) => {
        if (color.name === 'custom') {
            setSelectedSecondary('custom');
            setSecondaryTab('custom');
            onSecondaryColorChange?.(customSecondary);
        } else {
            setSelectedSecondary(color.value1);
            setSecondaryTab('standard');
            onSecondaryColorChange?.(color.value1);
        }
    };

    const handleCustomPrimaryChange = (color) => {
        setCustomPrimary(color);
        if (selectedPrimary === 'custom') {
            onPrimaryColorChange?.(color);
        }
    };

    const handleCustomSecondaryChange = (color) => {
        setCustomSecondary(color);
        if (selectedSecondary === 'custom') {
            onSecondaryColorChange?.(color);
        }
    };

    return (
        <Box sx={styles.overallContainer}>

            {/* Preview */}
            <Box sx={styles.preview}>
                <Typography sx={styles.title} color="grey.mainlight" mb={1}>
                    <Text>global.preview</Text>:
                </Typography>
                <Box sx={styles.previewImage}>
                    <PlayerShirt number={1} colors={playerColors} />
                </Box>
            </Box>

            {/* Color Selection */}
            <Stack sx={styles.colorButtons}>

                {/* Primary Color */}
                <Box sx={styles.colorButtonPrimary}>

                    <Typography sx={styles.title} color="grey.mainlight" mb={1}>
                        <Text>{unTranslated('Hauptfarbe')}:</Text>
                    </Typography>

                    <Button
                        onClick={() => setShowPrimaryColor(true)}
                        sx={[
                            styles.colorButton,
                            {backgroundColor: primaryColor}
                        ]}
                    />

                    <Dialog
                        open={showPrimaryColor}
                        title={unTranslated('Farbe auswählen')}
                        handleClose={() => setShowPrimaryColor(false)}
                    >
                        <Stack sx={styles.takeColorContainer}>
                            <ButtonBase
                                onClick={() => setShowPrimaryColor(false)}
                                sx={styles.takeColor}
                            >{unTranslated('Farbe übernehmen')}</ButtonBase>
                        </Stack>

                        <Stack sx={styles.tabContainer}>
                            <Tabs
                                value={primaryTab}
                                onChange={(_e, tabVal) => {
                                    setPrimaryTab(tabVal);
                                    if (tabVal === 'standard' && selectedPrimary === 'custom') {
                                        const fallback = colors[0]?.value1;
                                        if (fallback) {
                                            setSelectedPrimary(fallback);
                                            onPrimaryColorChange?.(fallback);
                                        }
                                    }

                                    if (tabVal === 'custom' && selectedPrimary !== 'custom') {
                                        setSelectedPrimary('custom');
                                        onPrimaryColorChange?.(customPrimary);
                                    }
                                }}
                            >
                                <Tab value="standard" label={<Typography><Text>{unTranslated('Standardfarbe')}</Text></Typography>} />
                                <Tab value="custom" label={<Typography><Text>{unTranslated('Eigene Farbe')}</Text></Typography>} />
                            </Tabs>
                        </Stack>

                        {primaryTab === 'standard' && (
                            <Stack sx={styles.colorCircleContainer}>
                                {colors.map((color) => {
                                    const isPremium = hasAccess('colors', color.name).access;
                                    return (
                                        <FlyoutButton
                                            key={`primary-${color.name}`}
                                            color={color.value1}
                                            selected={selectedPrimary === color.value1}
                                            onClick={() => handlePrimaryColorSelect(color)}
                                            isPremium={!isPremium}
                                        />
                                    );
                                })}
                            </Stack>)
                        }

                        {/* Custom Primary Color */}
                        {/* TODO access for premium users  */}
                        {primaryTab === 'custom' && (
                            <Stack sx={styles.tabContainerContent}>
                                <ColorPicker
                                    value={customPrimary}
                                    onChange={handleCustomPrimaryChange}
                                    label=""
                                    sx={{minWidth: 120}}
                                />
                            </Stack>)
                        }

                    </Dialog>

                </Box>

                {/* Secondary Color */}
                <Box>

                    <Typography sx={styles.title} color="grey.mainlight" mb={1}>
                        <Text>{unTranslated('Akzentfarbe')}:</Text>
                    </Typography>

                    <Button
                        onClick={() => setShowSecondaryColor(true)}
                        sx={[
                            styles.colorButton,
                            {backgroundColor: secondaryColor}
                        ]}
                    />

                    <Dialog
                        open={showSecondaryColor}
                        title={unTranslated('Farbe auswählen')}
                        handleClose={() => setShowSecondaryColor(false)}
                    >

                        <Stack sx={styles.takeColorContainer}>
                            <ButtonBase
                                onClick={() => setShowSecondaryColor(false)}
                                sx={styles.takeColor}
                            >{unTranslated('Farbe übernehmen')}</ButtonBase>
                        </Stack>

                        <Stack sx={styles.tabContainer}>
                            <Tabs
                                value={secondaryTab}
                                onChange={(_e, tabVal) => {
                                    setSecondaryTab(tabVal);
                                    if (tabVal === 'standard' && selectedSecondary === 'custom') {
                                        const fallback = colors[0]?.value1;
                                        if (fallback) {
                                            setSelectedSecondary(fallback);
                                            onSecondaryColorChange?.(fallback);
                                        }
                                    }

                                    if (tabVal === 'custom' && selectedSecondary !== 'custom') {
                                        setSelectedSecondary('custom');
                                        onSecondaryColorChange?.(customSecondary);
                                    }
                                }}
                            >
                                <Tab value="standard" label={<Typography><Text>{unTranslated('Standardfarbe')}</Text></Typography>} />
                                <Tab value="custom" label={<Typography><Text>{unTranslated('Eigene Farbe')}</Text></Typography>} />
                            </Tabs>
                        </Stack>

                        {secondaryTab === 'standard' && (
                            <Stack sx={styles.colorCircleContainer}>
                                {colors.map((color) => {
                                    const isPremium = hasAccess('colors', color.name).access;
                                    return (
                                        <FlyoutButton
                                            key={`secondary-${color.name}`}
                                            color={color.value1}
                                            selected={selectedSecondary === color.value1}
                                            onClick={() => handleSecondaryColorSelect({
                                                ...color,
                                                value1: color.value1
                                            })}
                                            isPremium={!isPremium}
                                        />
                                    );
                                })}
                            </Stack>)
                        }

                        {/* Custom Secondary Color */}
                        {/* TODO access for premium users  */}
                        {secondaryTab === 'custom' && (
                            <Stack sx={styles.tabContainerContent}>
                                <ColorPicker
                                    value={customSecondary}
                                    onChange={handleCustomSecondaryChange}
                                    label=""
                                    sx={{minWidth: 120}}
                                />
                            </Stack>)
                        }

                    </Dialog>

                </Box>
            </Stack>

        </Box>
    );
};

export default TeamColorPickerField;